// Raw fetch() call to the legacy /auth/refresh-token endpoint.
// Why not reuse axios: axios has its own 401 interceptor that tries to refresh
// on failure. If the refresh itself 401s through axios, the interceptor would
// re-enter and loop. A plain fetch sidesteps the interceptor chain entirely.

import * as auth from '@/utils/auth';

import type { RefreshFn, Tokens } from './AuthSession';
import { readRefreshTokenRaw, readUser } from './authStorage';
import { isImpersonating, readImpFp } from './tabStorage';

const HTTP_UNAUTHORIZED = 401;
const REFRESH_TIMEOUT_MS = 10_000;

export const createLegacyRefreshFn = (authApiBase: string): RefreshFn => {
  return async (): Promise<Tokens> => {
    const refreshToken = readRefreshTokenRaw();
    if (!refreshToken) {
      throw Object.assign(new Error('Cannot refresh: no refresh token in storage'), { status: HTTP_UNAUTHORIZED });
    }

    // current-user stays as the admin under impersonation, so the email-keyed fp lookup would miss; fall back to the per-tab imp-fp.
    let fp: string;
    if (isImpersonating()) {
      fp = readImpFp() ?? '';
    } else {
      const email = readUser();
      if (!email) {
        throw Object.assign(new Error('Cannot refresh: no current user in storage'), { status: HTTP_UNAUTHORIZED });
      }
      fp = auth.getFp(email) ?? '';
    }

    const response = await fetch(`${authApiBase}/auth/refresh-token`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken, fp }),
      signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
    });

    if (response.status === 401 || response.status === 403) {
      throw Object.assign(new Error('refresh rejected'), { status: response.status });
    }

    if (!response.ok) {
      throw Object.assign(new Error(`refresh failed: ${response.status}`), { status: response.status });
    }

    const data = (await response.json()) as { accessToken: string; refreshToken: string };
    return { accessToken: data.accessToken, refreshToken: data.refreshToken };
  };
};
